import { v4 as UUID } from 'uuid';

import { IdentityTeamTag, Location } from '@reg/shared/model';
import { DateFormat } from '@reg/shared/enum';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { convertToDateTime } from '@reg/shared/helper/dateTime-helper';

/**
 * Model to hold a calendar item related to a team
 */
export class IdentityCalendarItem {
    public id: string = null;
    public isNew: boolean = true;
    public isHosting: boolean = false;
    public eventId: string;
    public title: string = null;
    public description: string = null;
    public startDateTime: Date = null;
    public endDateTime: Date = null;
    public teamId: string = null;
    public warningMessage: string = null;
    public location: Location = null;
    public tags: IdentityTeamTag[] = [];

    constructor() {
        this.location = new Location();
        this.id = UUID();
        this.isNew = true;
    }

    public getFormattedDate(): string {
        if (this.startDateTime && this.endDateTime) {
            const startDate: string = format(this.startDateTime, DateFormat.MMDDYYYY);
            const endDate: string = format(this.endDateTime, DateFormat.MMDDYYYY);
            const timeZone: string = this.location.timeZone;

            if (startDate === endDate) {
                if (timeZone) {
                    const startTime: string = formatInTimeZone(this.startDateTime, timeZone, DateFormat.hma);
                    const endTime: string = formatInTimeZone(this.endDateTime, timeZone, DateFormat.hmaz);
                    return `${startTime} - ${endTime}`;
                }
                const startTime: string = format(this.startDateTime, DateFormat.hma);
                const endTime: string = format(this.endDateTime, DateFormat.hma);
                return `${startTime} - ${endTime}`;
            }
            return `${startDate} - ${endDate}`;
        }

        return null;
    }

    public clone(): IdentityCalendarItem {
        const clone: IdentityCalendarItem = new IdentityCalendarItem();
        clone.isNew = this.isNew;
        clone.id = this.id;
        clone.eventId = this.eventId;
        clone.title = this.title;
        clone.description = this.description;
        clone.teamId = this.teamId;
        clone.isHosting = this.isHosting;
        clone.warningMessage = this.warningMessage;

        if (this.startDateTime) {
            clone.startDateTime = new Date(this.startDateTime.getTime());
        }
        if (this.endDateTime) {
            clone.endDateTime = new Date(this.endDateTime.getTime());
        }
        if (this.location) {
            clone.location = this.location.clone();
        }
        if (this.tags) {
            clone.tags = this.tags.map((t: IdentityTeamTag) => t.clone());
        }

        return clone;
    }

    public removeTag(identityTeamTagId: string): void {
        const matchedTag: IdentityTeamTag = this.tags.filter((t: IdentityTeamTag) => t.id === identityTeamTagId)[0];

        if (matchedTag) {
            const index: number = this.tags.indexOf(matchedTag, 0);
            this.tags.splice(index, 1);
        }
    }

    public updateTag(identityTeamTag: IdentityTeamTag): void {
        const matchedTag: IdentityTeamTag = this.tags.filter((t: IdentityTeamTag) => t.id === identityTeamTag.id)[0];

        if (matchedTag) {
            matchedTag.name = identityTeamTag.name;
        }
    }

    /**
     * Serialize to a json api format
     */
    public serialize(): string {
        const json: any = {};
        json.data = {};
        json.data.type = 'calendarItem';
        json.data.attributes = {};
        json.data.relationships = {};
        json.data.id = this.id;
        json.data.attributes.eventId = this.eventId;
        json.data.attributes.title = this.title;
        json.data.attributes.isHosting = this.isHosting;
        json.data.attributes.description = this.description;
        json.data.attributes.warningMessage = this.warningMessage;
        json.data.attributes.startDateTimeUtc = this.startDateTime.toISOString();
        json.data.attributes.endDateTimeUtc = this.endDateTime.toISOString();
        json.data.attributes.location = null;

        if (this.location) {
            json.data.attributes.location = this.location.serialize();
        }

        json.data.relationships.team = { data: { id: this.teamId, type: 'team' } };

        json.data.relationships.teamTags = { data: [] };
        if (this.tags) {
            for (const tag of this.tags) {
                json.data.relationships.teamTags.data.push({ id: tag.id, type: 'teamTag' });
            }
        }

        return JSON.stringify(json);
    }

    /**
     * Deserialize from json api format
     */
    public deserialize(json: Object, included: Object[] = null): IdentityCalendarItem {
        const data: Object = json['data'] || json;
        const attrs: Object = data['attributes'] || [];
        const relationships: Object = data['relationships'] || [];
        included = included || json['included'] || [];

        this.isNew = false;
        this.id = data['id'];
        this.eventId = attrs['eventId'];
        this.title = attrs['title'];
        this.description = attrs['description'];
        this.startDateTime = convertToDateTime(attrs['startDateTime']);
        this.endDateTime = convertToDateTime(attrs['endDateTime']);
        this.location = (new Location()).deserialize(attrs['location']);
        this.teamId = attrs['teamId'];
        this.warningMessage = attrs['warningMessage'];
        this.isHosting = attrs['isHosting'] === true;

        const tagLinks: Object[] = relationships['teamTags'];
        if (tagLinks &&
            included &&
            tagLinks['data']) {
            this.tags = [];

            for (const tagLink of tagLinks['data']) {
                const tag: Object = included.filter((i: any) => i.id === tagLink.id)[0];
                if (tag) {
                    this.tags.push((new IdentityTeamTag()).deserialize(tag));
                }
            }
        }

        return this;
    }
}
